import React, { FC, useEffect, useState } from 'react';
import clsx from 'clsx';
import { fetchCouriers } from 'fetch/fetchCouriers';
import { fetchInvoice, InvoiceLineWithKey } from 'fetch/reportFetch';
import { Helmet } from 'react-helmet';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { ComboBoxComponent } from 'ReusableComponents/ComboBox/ComboBoxComponent';
import { ComboBoxItem } from 'ReusableComponents/ComboBox/ComboBoxComponent.types';
import { CourierJson } from 'types/bundleTypes';
import { Region } from 'types/orderTypes';
import CourierInvoiceTable from './CourierInvoiceTable';
import {
  getEndDate,
  getStartDate,
} from 'components/OrderListTable/CalendarFetch/CalendarFetchByPeriods';
import './CourierInvoice.scss';
import { fetchIsMvaRegistered } from 'utils/enhetsregisteretUtil';
import { fetchMyAccount } from 'fetch/fetchAccounts';
import { fetchMyself } from 'fetch/fetchUsers';
import { CircleProgressComponent } from '../../ReusableComponents/Progress/ProgressComponent';
import { nbsp } from 'utils/strings';
import { fetchRegionalRatings, getFirstPeriod, getSecondPeriod } from './utils';
import { useCourierInvoiceLines } from './hooks';
import CourierInvoiceSummary from './CourierInvoiceSummary';
import { ButtonComponent } from 'ReusableComponents/Button/ButtonComponent';
import { roundToTwo } from '../../components/OrderListTable/utils/roundToTwo';

const CourierInvoice: FC<{
  region: Region;
  isDispatcher?: boolean;
}> = ({ region, isDispatcher }) => {
  const [startDate, setStartDate] = useState(getStartDate());
  const [endDate, setEndDate] = useState(getEndDate());
  const [selectedCourierName, setSelectedCourierName] = useState<string>();
  const [selectedCourierId, setSelectedCourierId] = useState<string>();
  const [isLoading, setIsloading] = useState<boolean>(false);
  const [displayPeriod, setDisplayPeriod] = useState(
    startDate.date() === 1 ? 1 : 2
  );
  const [allCouriers, setAllCouriers] = useState<{ [id: string]: CourierJson }>(
    {}
  );
  const [courierDropdownItems, setCourierDropdownItems] = useState<
    ComboBoxItem[]
  >([]);
  const [rawInvoiceLines, setRawInvoiceLines] = useState<InvoiceLineWithKey[]>(
    []
  );
  const [companyName, setCompanyName] = useState<string>();
  const [companyNumber, setCompanyNumber] = useState<string>();
  const [isVatRegistered, setIsVatRegistered] = useState<boolean>(false);
  const [courierRatings, setCourierRatings] = useState<{
    [region: string]: number;
  }>({});

  const invoiceLines = useCourierInvoiceLines(rawInvoiceLines);

  // Dispatcher: fetch all courier info
  // Courier: fetch info about given the logged in user and affiliated company
  useEffect(() => {
    (async () => {
      if (isDispatcher) {
        setIsloading(true);
        const courierList = await fetchCouriers();
        setIsloading(false);
        const couriers: { [id: string]: CourierJson } = {};
        const courierItems = [] as ComboBoxItem[];
        courierList.forEach(courier => {
          courierItems.push({
            name: courier.fullName,
            value: courier.id.toString(),
          });
          couriers[courier.id] = courier;
        });

        courierItems.sort((a, b) => a.name.localeCompare(b.name));

        setAllCouriers(couriers);
        setCourierDropdownItems(courierItems);
      } else {
        // If not user is dispatcher, then 'me' is a logged in courier
        setIsloading(true);
        const me = await fetchMyself();
        const myCompany = await fetchMyAccount();
        const vatRegistration = await fetchIsMvaRegistered(
          myCompany.companyNumber
        );
        setIsloading(false);
        setSelectedCourierName(me.fullName);
        setCompanyName(myCompany.name);
        setCompanyNumber(myCompany.companyNumber);
        setIsVatRegistered(vatRegistration);
      }
    })();
  }, [isDispatcher]);

  // Check vat registration when courier selection is available
  useEffect(() => {
    if (isDispatcher) {
      const company = allCouriers[selectedCourierId];
      const orgNum = company?.companyNumber;
      setCompanyNumber(orgNum);
      setCompanyName(company?.companyName);
      if (orgNum != null) {
        fetchIsMvaRegistered(orgNum)
          .then(vatReg => setIsVatRegistered(vatReg))
          .catch(e => console.error(e));
      }
    }
  }, [isDispatcher, allCouriers, selectedCourierId]);

  // Fetch invoicelines
  // Also fetch courier rating
  useEffect(() => {
    (async () => {
      if (isDispatcher) {
        if (selectedCourierId != null) {
          setIsloading(true);

          const lines = await fetchInvoice(
            startDate,
            endDate,
            selectedCourierId
          );
          lines.sort(
            (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
          );
          setRawInvoiceLines(lines);

          const regions = [
            ...new Set(lines.map(line => line.operationalRegion)),
          ];
          const regionalRatings = await fetchRegionalRatings(
            selectedCourierId,
            startDate,
            endDate,
            regions
          );
          setCourierRatings(regionalRatings);

          setIsloading(false);
        } else {
          // Reset lines state when searchbox is cleared
          setRawInvoiceLines([]);
        }
      } else {
        // isDispatcher is not true, the user is a courier.
        setIsloading(true);

        const lines = await fetchInvoice(startDate, endDate);
        setRawInvoiceLines(lines);

        const regions = [...new Set(lines.map(line => line.operationalRegion))];
        const regionalRatings = await fetchRegionalRatings(
          selectedCourierId,
          startDate,
          endDate,
          regions
        );
        setCourierRatings(regionalRatings);

        setIsloading(false);
      }
    })();
  }, [isDispatcher, selectedCourierId, startDate, endDate]);

  const setNewPeriod = (newPeriod: number) => {
    if (newPeriod === 1) {
      const [start, end] = getFirstPeriod(startDate);
      setStartDate(start);
      setEndDate(end);
      setDisplayPeriod(1);
    } else if (newPeriod === 2) {
      const [start, end] = getSecondPeriod(startDate);
      setStartDate(start);
      setEndDate(end);
      setDisplayPeriod(2);
    } else {
      if (newPeriod < 1) {
        // previous month
        const previousMonth = startDate.subtract(1, 'month');
        const [start, end] = getSecondPeriod(previousMonth);
        setStartDate(start);
        setEndDate(end);
        setDisplayPeriod(2);
      } else if (newPeriod > 2) {
        // next month
        const nextMonth = startDate.add(1, 'month');
        const [start, end] = getFirstPeriod(nextMonth);
        setStartDate(start);
        setEndDate(end);
        setDisplayPeriod(1);
      }
    }
  };

  const nextPeriod = () => setNewPeriod(displayPeriod + 1);
  const previousPeriod = () => setNewPeriod(displayPeriod - 1);

  // Øre -> Krone = amount * 0.01
  const totalAmount =
    rawInvoiceLines.reduce(
      (total, line) => total + line.count * line.price,
      0
    ) * 0.01;
  const vatAmount = isVatRegistered ? 0.25 * totalAmount : 0;
  const totalAmountWithVat = isVatRegistered ? 1.25 * totalAmount : totalAmount;

  return (
    <>
      <Helmet>
        <title>Reports - Invoice Courier</title>
      </Helmet>
      <div className="invoicePageBackground">
        <div className="courierInvoicePage">
          <div>
            <p
              className={clsx(
                'courierInvoicePage__onlyPrint',
                'courierInvoicePage__title'
              )}
            >
              Courier Period Report
            </p>
            <div className="courierInvoicePage__dataSelect">
              <p
                className={clsx(
                  'courierInvoicePage__onlyPrint',
                  'courierInvoicePage__courierName'
                )}
              >
                {selectedCourierName ?? ''}
              </p>
              {isDispatcher && (
                <ComboBoxComponent
                  id=""
                  className="courierInvoicePage__dataSelect__courierSelect"
                  placeholder="Select courier"
                  comboBoxItems={courierDropdownItems}
                  onChange={(_, item) => {
                    const comboboxItem = item as ComboBoxItem;
                    setSelectedCourierId(comboboxItem?.value);
                    setSelectedCourierName(comboboxItem?.name);
                  }}
                />
              )}
            </div>
          </div>
          {isLoading ? (
            <CircleProgressComponent
              className="courierInvoicePage__spinner"
              variant={'indeterminate'}
            />
          ) : selectedCourierName ? (
            <>
              <CompanyInfo
                companyName={companyName}
                courierName={selectedCourierName}
                orgNumber={companyNumber}
                isVat={isVatRegistered}
              />
              <div className="courierInvoicePage__courierPeriod">
                <FaChevronLeft
                  className="courierInvoicePage__nonPrint"
                  onClick={previousPeriod}
                />
                <p>
                  Period {displayPeriod} -{' '}
                  {`${startDate.format('MMMM YYYY')} - ${startDate.format(
                    'DD'
                  )} - ${endDate.format('DD')}`}
                </p>
                <FaChevronRight
                  className="courierInvoicePage__nonPrint"
                  onClick={nextPeriod}
                />
              </div>
              <span className="courierInvoicePage__nonPrint">
                <ButtonComponent
                  className="courierInvoicePage__printButton"
                  onClick={() => window.print()}
                >
                  Download PDF
                </ButtonComponent>
              </span>
              <p
                style={{
                  margin: '15px 0',
                  fontSize: '15px',
                  fontWeight: 400,
                  color: '#125729',
                }}
              >
                Due to invoicing calculations, report data are subject to
                changes up to 3 days after period end
              </p>
              <CourierInvoiceSummary
                isVatRegistered={isVatRegistered}
                invoiceLines={invoiceLines}
                courierRatings={courierRatings}
              />
              <h1>Daily breakdown</h1>
              <CourierInvoiceTable
                invoiceLines={invoiceLines}
                reportStartDate={startDate}
              />
              <div className="courierInvoicePage__totalAmounts">
                <div>
                  <p className="courierInvoicePage__totalAmounts__key">
                    Total excl. VAT
                  </p>
                  <p className="courierInvoicePage__totalAmounts__key">
                    VAT 25%
                  </p>
                  <p className="courierInvoicePage__totalAmounts__key">
                    Total incl. VAT
                  </p>
                </div>
                <div>
                  <p className="courierInvoicePage__totalAmounts__value">
                    {roundToTwo(totalAmount)}
                  </p>
                  <p className="courierInvoicePage__totalAmounts__value">
                    {roundToTwo(vatAmount)}
                  </p>
                  <p className="courierInvoicePage__totalAmounts__value">
                    {roundToTwo(totalAmountWithVat)}
                  </p>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
    </>
  );
};

const CompanyInfo: FC<{
  courierName?: string;
  companyName?: string;
  orgNumber?: string;
  isVat?: boolean;
}> = ({ companyName, courierName, orgNumber, isVat }) => {
  return (
    <div className="companyInfoBox">
      <p>
        <em>{companyName ?? 'Not registered'}</em> (Courier:{' '}
        {courierName ?? nbsp})
      </p>
      <p>
        Org. no: {orgNumber ?? nbsp} VAT reg: {isVat ? 'Yes' : 'No'}
      </p>
    </div>
  );
};

export default CourierInvoice;
